import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  NoPermissionState,
  PageHeader,
} from '@stayafrica/ui';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  BarChart3,
  Building2,
  CalendarDays,
  ChevronRight,
  Coins,
  Users,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import {
  REPORTS_ENTRY_PERMISSION,
  REPORT_FAMILIES,
  reportsOfFamily,
  type ReportFamily,
} from '@/features/reports/catalog';

interface ReportsSearch {
  /** Ouvre la page depuis ⌘K « Exporter le rapport … » (met l'export en avant). */
  export?: boolean;
}

export const Route = createFileRoute('/_app/reports/')({
  validateSearch: (search: Record<string, unknown>): ReportsSearch =>
    search['export'] === true || search['export'] === 'true' ? { export: true } : {},
  component: ReportsIndexPage,
});

const FAMILY_ICONS: Record<ReportFamily, typeof BarChart3> = {
  ACTIVITY: CalendarDays,
  FINANCE: Coins,
  CUSTOMERS: Users,
  RESOURCES: Building2,
};

const FAMILY_TONES: Record<ReportFamily, 'violet' | 'blue' | 'green' | 'amber'> = {
  ACTIVITY: 'blue',
  FINANCE: 'amber',
  CUSTOMERS: 'green',
  RESOURCES: 'violet',
};

/**
 * Accueil des rapports (SCR-100) : les 11 rapports du catalogue, groupés par
 * famille et filtrés par permission (`report.operational` / `report.financial`).
 */
function ReportsIndexPage() {
  const { t } = useTranslation(['reports', 'common']);
  const session = useSession();
  const search = Route.useSearch();

  // `GET /reports/:kind` exige `report.operational` quel que soit le rapport ;
  // `report.financial` seule n'ouvre rien (voir `features/reports/catalog.ts`).
  const canAny = session.can(REPORTS_ENTRY_PERMISSION);

  if (!canAny) {
    return (
      <Container>
        <PageHeader title={t('reports:title')} subtitle={t('reports:subtitle')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const families = REPORT_FAMILIES.map((family) => ({
    family,
    reports: reportsOfFamily(family, (permission) => session.can(permission)),
  })).filter((entry) => entry.reports.length > 0);

  return (
    <Container>
      <PageHeader
        title={t('reports:title')}
        subtitle={t('reports:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('reports:title') },
        ]}
        actions={
          session.can('report.export') && search.export ? (
            <Badge tone="violet">{t('reports:exportHint')}</Badge>
          ) : null
        }
      />

      <div className="stack">
        {families.length === 0 ? (
          <EmptyState
            icon={<BarChart3 className="ico ico-24" aria-hidden="true" />}
            title={t('reports:emptyTitle')}
            description={t('reports:emptyBody')}
          />
        ) : (
          families.map(({ family, reports }) => {
            const Icon = FAMILY_ICONS[family];
            return (
              <Card key={family}>
                <CardHeader
                  title={t(`reports:families.${family}`)}
                  icon={<Icon className="ico ico-16" aria-hidden="true" />}
                  iconTone={FAMILY_TONES[family]}
                  actions={<Badge tone="neutral">{reports.length}</Badge>}
                />
                <CardBody>
                  <div className="grid grid-3">
                    {reports.map((report) => (
                      <Link
                        key={report.kind}
                        className="kpi"
                        to="/reports/$kind"
                        params={{ kind: report.kind.toLowerCase() }}
                      >
                        <div className="kpi__top">
                          <span className="kpi__ico" aria-hidden="true">
                            {report.permission === 'report.financial' ? (
                              <Wallet className="ico ico-16" />
                            ) : (
                              <BarChart3 className="ico ico-16" />
                            )}
                          </span>
                          <span className="kpi__label">
                            {t(`reports:kinds.${report.kind}.title`)}
                          </span>
                        </div>
                        <p className="t-13 muted mb-0">
                          {t(`reports:kinds.${report.kind}.description`)}
                        </p>
                        <div className="kpi__foot">
                          <span>{t('reports:open')}</span>
                          <ChevronRight className="ico ico-14" aria-hidden="true" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardBody>
              </Card>
            );
          })
        )}
      </div>
    </Container>
  );
}
